import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, X } from "lucide-react";

// In-app banner shown when a new message arrives while the app is OPEN.
// (The OS-level banner for background/closed is handled by the service worker.)
export default function NotificationCenter() {
  const { subscribe } = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [banner, setBanner] = useState(null); // {name, text, matchId}
  const timer = useRef(null);

  const show = useCallback((b) => {
    clearTimeout(timer.current);
    setBanner(b);
    timer.current = setTimeout(() => setBanner(null), 4500);
  }, []);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    return subscribe((data) => {
      if (data.type !== "message" || !data.message) return;
      if (data.message.sender_id === user.user_id) return; // own message
      // If already inside that chat, the live thread shows it — no banner.
      if (location.pathname === `/chat/${data.match_id}`) return;
      show({ name: data.sender_name || "New match", text: data.message.text, matchId: data.match_id });
    });
  }, [subscribe, user, location.pathname, show]);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!banner) return null;

  const open = () => {
    setBanner(null);
    navigate(`/chat/${banner.matchId}`);
  };

  return (
    <div className="absolute top-3 inset-x-3 z-[80] pointer-events-none flex justify-center">
      <button
        data-testid="in-app-notification"
        onClick={open}
        className="pointer-events-auto w-full max-w-sm animate-float-up flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#1D1829]/95 backdrop-blur-xl border border-rose-500/40 shadow-2xl shadow-rose-500/20 text-left"
      >
        <span className="w-10 h-10 rounded-full grid place-items-center bg-gradient-to-br from-rose-500 to-amber-400 shrink-0">
          <MessageCircle className="w-5 h-5 text-white" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-white truncate">{banner.name} <span className="font-normal text-slate-300">sent you a message</span></span>
          <span className="block text-xs text-slate-400 truncate mt-0.5">{banner.text}</span>
        </span>
        <span
          role="button"
          data-testid="notification-close"
          onClick={(e) => { e.stopPropagation(); setBanner(null); }}
          className="w-7 h-7 grid place-items-center text-slate-400 shrink-0"
        >
          <X className="w-4 h-4" />
        </span>
      </button>
    </div>
  );
}
